import React, { Component, Fragment } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Trans, withTranslation } from 'react-i18next';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import sortBy from 'lodash/sortBy';

import { Button } from 'formlabs-web-components/Link';
import { Header } from 'formlabs-web-components/Type';

import CheckoutButton from './CheckoutButton';
import ProductImage from './ProductImage';
import UpsellProducts from './UpsellProducts';
import CenterModal from '../modal/CenterModal';

import { addToCart as addToCartAction } from '../../ducks/cart';
import useFetchProducts from '../../hooks/useFetchProducts';

import './AddToCart.module.css';

const preventDefaultHandler = event => {
  event.preventDefault();
};

const Upsell = ({ sku, storeId, t }) => {
  const { loading, data } = useFetchProducts(storeId, sku);

  if (loading || !data || !data.length) {
    return null;
  }

  const productLinks = data[0].product_links;
  const upsellProducts = productLinks
    ? sortBy(
        productLinks
          .filter(link => link.link_type === 'upsell')
          .map(link => link.linked_product_sku),
        ['position']
      )
    : null;

  if (!upsellProducts || !upsellProducts.length) {
    return null;
  }

  return (
    <div styleName="upsell">
      <div styleName="upsell-header">
        <Header level={2} displayAs={4} weight={400}>
          {t("Don't forget")}
        </Header>
      </div>
      <UpsellProducts skus={upsellProducts} />
    </div>
  );
};

class AddToCart extends Component {
  quickbutton = React.createRef();

  state = {
    error: null,
    showModal: false
  };

  closeModal = event => {
    if (event) {
      event.preventDefault();
    }
    if (this.state.unlisten) {
      this.state.unlisten();
    }
    this.setState({ showModal: false, error: null });
    this.quickbutton.current.removeEventListener(
      'click',
      preventDefaultHandler
    );
  };

  openModal = error => {
    this.setState({
      unlisten: this.props.history.listen(() => {
        this.closeModal();
      }),
      showModal: true,
      error
    });
  };

  handleClick = async event => {
    this.quickbutton.current.addEventListener('click', preventDefaultHandler, {
      passive: false
    });

    const { addToCart, amount, sku, bundleOptions, quickAdd } = this.props;

    const item = {
      sku,
      qty: amount
    };

    const context = quickAdd ? 'quick-add' : 'normal';

    addToCart(item, bundleOptions, context, (error = null) => {
      this.openModal(error);
    });
  };

  render() {
    const { name, t, title, quickAdd, sku, loading, storeId } = this.props;
    const { showModal, error } = this.state;

    const buttonTitle = title || t('Buy now');

    return (
      <Fragment>
        <Button
          to="#"
          onClick={this.handleClick}
          small={quickAdd}
          loading={loading}
          ref={this.quickbutton}
        >
          {quickAdd ? (
            <span styleName="quick">
              <span>{t('Add')}</span>
              <span>+</span>
            </span>
          ) : (
            buttonTitle
          )}
        </Button>

        {showModal && (
          <CenterModal
            id="addToCart"
            onClose={() => {
              this.closeModal();
            }}
          >
            {error ? (
              <p styleName="modal-title">
                <strong>{t('Error')}:</strong>{' '}
                {error.response ? error.response.message : error.message}
              </p>
            ) : (
              <p styleName="modal-title">
                <ProductImage sku={sku} alt={name} />
                <span>
                  <Trans>
                    <strong>{{ name }}</strong> has been added to your cart.
                  </Trans>
                </span>
              </p>
            )}

            <div styleName="actions">
              <CheckoutButton withArrow />
              <Button to="#" onClick={this.closeModal} color="light">
                {t('Continue shopping')}
              </Button>
            </div>

            <Upsell sku={sku} storeId={storeId} t={t} />
          </CenterModal>
        )}
      </Fragment>
    );
  }
}

AddToCart.propTypes = {
  amount: PropTypes.number.isRequired,
  sku: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  quickAdd: PropTypes.bool
};

AddToCart.defaultProps = {
  bundleOptions: null,
  title: null,
  quickAdd: false
};

export default compose(
  connect(
    (state, ownProps) => {
      return {
        storeId: state.store.id,
        loading: state.cart.loading === ownProps.sku
      };
    },
    dispatch => {
      return {
        addToCart: (item, bundleOptions, context, callback) => {
          dispatch(addToCartAction(item, bundleOptions, context, callback));
        }
      };
    }
  ),
  withTranslation(),
  withRouter
)(AddToCart);
